import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getAnnounceDetails } from 'services/announce'

const PAGE_SIZE = 5

// Wording and colours copied from Android so the two clients read identically
// (MoneypoolAnnounceReceiverAdapter.bindTo). Raw values come from the announce
// microservice (Sms.Status / BaleMessage.Status).
const SMS_STATUS: { [k: string]: { label: string; className: string } } = {
    IN_PROGRESS: { label: 'در حال انجام', className: 'text-gray-500' },
    TO_MOKHABERAT: { label: 'در مخابرات', className: 'text-gray-500' },
    FAILED: { label: 'ارسال ناموفق', className: 'text-red-500' },
    UNDELIVERED: { label: 'ارسال ناموفق', className: 'text-red-500' },
    DELIVERED: { label: 'ارسال موفق', className: 'text-green-1' },
    LAGHV: { label: 'لغو شده', className: 'text-red-500' },
    BLOCKED: { label: 'مسدود شده', className: 'text-red-500' },
}
const BALE_STATUS: { [k: string]: { label: string; className: string } } = {
    pending: { label: 'در انتظار ارسال', className: 'text-gray-500' },
    delivered: { label: 'ارسال موفق', className: 'text-green-1' },
    unsuccessful: { label: 'ارسال ناموفق', className: 'text-red-500' },
}

// Delivery, as the two status vocabularies spell it. Anything else — queued,
// «در مخابرات» — is neither yet, and counting it as either would make a
// still-sending announcement look finished.
const DELIVERED = new Set(['DELIVERED', 'delivered'])
const FAILED = new Set([
    'FAILED',
    'UNDELIVERED',
    'LAGHV',
    'BLOCKED',
    'unsuccessful',
])

/** Messages, not people: one receiver can be sent both an SMS and a Bale message. */
const countStatuses = (receivers: IAnnounceReceiver[]) => {
    let delivered = 0
    let failed = 0
    receivers.forEach((r) => {
        ;[r.sms_message, r.bale_message].forEach((message) => {
            if (!message) return
            if (DELIVERED.has(message.status)) delivered += 1
            else if (FAILED.has(message.status)) failed += 1
        })
    })
    return { delivered, failed }
}

// The API reports cost in rial; Android shows تومان (cost / 10) and «رایگان»
// for zero rather than a bare 0.
function toman(rial: number | undefined | null) {
    if (!rial) return 'رایگان'
    return `${Math.round(rial / 10).toLocaleString()} تومان`
}

function ChannelStatus({
    label,
    status,
    map,
    cost,
}: {
    label: string
    status: string
    map: { [k: string]: { label: string; className: string } }
    cost: number
}) {
    const s = map[status]
    return (
        <div className="min-w-[86px] text-center">
            <p className="text-xs text-gray-400">{label}</p>
            <p className={`text-xs ${s ? s.className : 'text-gray-500'}`}>
                {s ? s.label : status}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">{toman(cost)}</p>
        </div>
    )
}

function ReceiverCard({ r }: { r: IAnnounceReceiver }) {
    return (
        <div className="mb-3 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
            <p className="font-bold text-text-body">
                {r.name || r.phone_number}
            </p>
            {r.name && r.phone_number && (
                <p dir="ltr" className="mb-1 text-left text-xs text-gray-400">
                    {r.phone_number}
                </p>
            )}
            <div className="mt-2 border-t border-[#F2F2F2] pt-2">
                <p className="mb-1 text-xs text-gray-400">وضعیت دریافت:</p>
                <div className="flex gap-3">
                    {r.sms_message && (
                        <ChannelStatus
                            label="پیامک"
                            status={r.sms_message.status}
                            map={SMS_STATUS}
                            cost={r.sms_message.cost}
                        />
                    )}
                    {r.bale_message && (
                        <ChannelStatus
                            label="بله"
                            status={r.bale_message.status}
                            map={BALE_STATUS}
                            cost={r.bale_message.cost}
                        />
                    )}
                    {!r.sms_message && !r.bale_message && (
                        <p className="text-sm text-gray-400">—</p>
                    )}
                </div>
            </div>
        </div>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between py-1">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-sm text-text-body">{value}</span>
        </div>
    )
}

// Everything Android shows above the recipient list: the message text, the
// category, which channels carried it, when, and what it cost us.
function AnnounceSummary({ a }: { a: IAnnounceItem }) {
    const channels = a.channels?.map((c) => c.title).join('، ') || '—'
    return (
        <div className="mb-4">
            <p className="mb-2 text-sm font-bold text-text-body">متن پیام:</p>
            <p className="mb-4 whitespace-pre-line rounded-xl border border-dashed border-[#D9D9D9] p-3 text-sm leading-relaxed text-text-body">
                {a.text}
            </p>
            <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
                <div className="flex items-baseline justify-between py-1">
                    <span className="text-sm text-gray-500">عنوان:</span>
                    <span className="rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                        {a.tags}
                    </span>
                </div>
                <InfoRow label="ارسال از طریق:" value={channels} />
                <InfoRow label="تاریخ ایجاد:" value={a.created_at} />
                {a.receivers_count > 0 && (
                    <InfoRow
                        label="تعداد گیرندگان:"
                        value={`${a.receivers_count.toLocaleString()} نفر`}
                    />
                )}
                <InfoRow label="هزینه پیامک:" value={toman(a.total_sms_cost)} />
                <InfoRow label="هزینه بله:" value={toman(a.total_bale_cost)} />
            </div>
        </div>
    )
}

export default function AnnounceDetail() {
    const { id, announceId } = useParams()
    const [summary, setSummary] = useState<IAnnounceItem | null>(null)
    const [items, setItems] = useState<IAnnounceReceiver[]>([])
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const reported = useRef<boolean>(false)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (!id || !announceId) return
            reset ? setLoading(true) : setLoadingMore(true)
            try {
                const res = await getAnnounceDetails(id, announceId, pageNumber)
                const batch = res?.announce_messages || []
                // keep the first header we got so paging never blanks it out
                if (res?.announce) setSummary(res.announce)
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasMore(batch.length >= PAGE_SIZE)

                // Once, when the summary lands. What this announcement cost is
                // on the header and therefore exact; the delivery counts are
                // over the receivers loaded so far — the first page — so read
                // them against `receivers_count`, never as the total.
                //
                // No `phone_number` and no `name`: the recipient list is a
                // fund's membership roll and has no business leaving the device.
                if (reset && res?.announce && !reported.current) {
                    reported.current = true
                    const { delivered, failed } = countStatuses(batch)
                    posthog.capture('announce_detail_viewed', {
                        moneypool_id: Number(id),
                        announce_id: Number(announceId),
                        receivers_count: res.announce.receivers_count,
                        sms_cost_rial: res.announce.total_sms_cost,
                        bale_cost_rial: res.announce.total_bale_cost,
                        delivered_count: delivered,
                        failed_count: failed,
                    })
                }
            } catch {
                if (reset) setItems([])
                setHasMore(false)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id, announceId]
    )

    useEffect(() => {
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <div className="flex h-full flex-col bg-white">
            <Header title="جزئیات اطلاع‌رسانی" />
            <div className="grow overflow-y-auto px-3 py-3">
                {loading ? (
                    <DotsLoading />
                ) : (
                    <>
                        {summary && <AnnounceSummary a={summary} />}
                        <p className="mb-2 text-sm font-bold text-green-1">
                            دریافت‌کنندگان:
                        </p>
                        {items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                                <p>گیرنده‌ای برای این اطلاع‌رسانی یافت نشد</p>
                            </div>
                        ) : (
                            <>
                                {items.map((r, i) => (
                                    <ReceiverCard
                                        key={`${r.phone_number}-${i}`}
                                        r={r}
                                    />
                                ))}
                                {hasMore && (
                                    <button
                                        onClick={onLoadMore}
                                        disabled={loadingMore}
                                        className="my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                                    >
                                        {loadingMore
                                            ? 'در حال بارگذاری...'
                                            : 'موارد بیشتر'}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
